import { HeadContent, Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { css } from 'styled-system/css';
import type { PuzzleRepository, PuzzleSolver, WordsRepository } from '@/application';
import type { AnalyticsPort } from '@/application/analytics';
import type { AuthClient } from '@/application/auth';
import type { BillingClient } from '@/application/billing';
import type { GameClient, LobbyClient } from '@/application/game';
import type { LobbyJoinCodeStash } from '@/application/session/LobbyJoinCodeStash';
import type { SessionClient } from '@/application/session/SessionClient';
import type { ProgressSyncService } from '@/application/progress';
import type { SoloEntriesStore } from '@/application/solo/SoloEntriesStore';
import type { SurveyAnonStore, SurveyClient } from '@/application/survey';
import type { CorrectionClient } from '@/application/correction';
import type { TourSeenStore } from '@/application/tour/TourSeenStore';
import type { ThemeStore } from '@/application/session/ThemeStore';
import type { SoundStore } from '@/application/session/SoundStore';
import type { SkipFilledStore } from '@/application/session/SkipFilledStore';
import type { SoundPlayer } from '@/application/session/SoundPlayer';
import type { Pseudonym, SessionId } from '@/domain/game';
import { SparrowState } from '@/ui/v2/SparrowState';
import { NOT_FOUND_COPY, useNotFoundDocumentTitle } from '@/ui/v2/NotFoundScreen';
import { sparrowFlightScene } from '@/ui/v2/SparrowScenes';
import { AnnouncerProvider } from '@/ui/components/a11y/Announcer';
import { Toast, ToastProvider } from '@/ui/components/primitives';
import { t } from '@/ui/i18n';

// Router context surface — every route loader receives this object as
// `ctx.context`. The composition root (`main.tsx`) is the only place
// that wires concrete adapters, keeping `ui/` free of `infrastructure/`
// imports (ADR-0002 §7).
//
// Multiplayer adapters (`lobbyClient`, `gameClient`, `getSession`,
// `setPseudonym`) are optional: they are only present when the runtime
// feature flag (ADR-0018 §10) enables the lobby route. Routes that
// consume them are only registered when the flag is on, so they may
// safely assert non-null at the call site.
//
// `setPseudonym` is the write-side counterpart to `getSession`'s
// pseudonym field — exposed here so `ui/routes/` can persist a rename
// without importing `infrastructure/session/` directly (boundary rule
// per ADR-0002 §7). The composition root in `main.tsx` wires it to
// `localStorageSession.setPseudonym`.
export interface AppSession {
  readonly sessionId: SessionId;
  readonly pseudonym: Pseudonym;
}

export interface AppRouterContext {
  readonly puzzleRepository: PuzzleRepository;
  readonly puzzleSolver: PuzzleSolver;
  // Optional: route-level Vitest fixtures can omit it; teaser falls back to inline pairs (ADR-0073).
  readonly wordsRepository?: WordsRepository;
  readonly sessionClient: SessionClient;
  readonly soloEntriesStore: SoloEntriesStore;
  // Solo-progress sync side-channel (ADR-0075); optional so Vitest fixtures can omit it.
  readonly progressSyncService?: ProgressSyncService;
  readonly tourSeenStore: TourSeenStore;
  // Theme preference port (ADR-0088); optional so route-level Vitest fixtures can omit it.
  readonly themeStore?: ThemeStore;
  // Grid sound-effects preference port; optional so route-level Vitest fixtures can omit it.
  readonly soundStore?: SoundStore;
  // Skip-filled-cells preference port; optional so route-level Vitest fixtures can omit it.
  readonly skipFilledStore?: SkipFilledStore;
  // Grid sound-effects player port; optional so route-level Vitest fixtures can omit it.
  readonly soundPlayer?: SoundPlayer;
  // Phase 5 — identity-api adapter + a thin getter over the anon
  // localStorage pseudonym. Wired in `main.tsx`; `ui/` consumers read
  // both via this context so `infrastructure/` stays out of `ui/`.
  // Optional so route-level Vitest fixtures don't have to stub them
  // (the auth surface is composed at the root via <AuthProvider>;
  // tests that don't wrap in <AuthProvider> simply omit the header
  // auth slot — see `AppHeader.tsx`).
  readonly authClient?: AuthClient;
  readonly getPseudonym?: () => string;
  readonly lobbyClient?: LobbyClient;
  readonly gameClient?: GameClient;
  readonly getSession?: () => AppSession;
  readonly setPseudonym?: (pseudonym: Pseudonym) => void;
  // Survey-api adapter (ADR-0056). Optional; ContribuerPage degrades gracefully when undefined.
  readonly surveyClient?: SurveyClient;
  // Grid corrections adapter (ADR-0108). Optional; the /signalements queue hides "Corriger" when undefined.
  readonly correctionClient?: CorrectionClient;
  // Anon-rated dedup store (ADR-0056). Optional for Vitest fixtures.
  readonly surveyAnonStore?: SurveyAnonStore;
  // Analytics port (ADR-0025). Optional; defaults to a no-op in tests.
  readonly analytics?: AnalyticsPort;
  // Billing-api adapter (ADR-0078). Optional so route-level Vitest fixtures can omit it.
  readonly billingClient?: BillingClient;
  // ADR-0027: per-tab one-shot stash that the `/join/$code` route and
  // the Accueil "Rejoindre" submit populate, and the lobby route's
  // WS-open consumes. Optional alongside the multiplayer adapters
  // because every route that reads it is gated by the same flag.
  readonly lobbyJoinCodeStash?: LobbyJoinCodeStash;
}

// v2 jade surround; self-contained (no router/auth hooks) so it renders even when the route tree fails.
const errorPageStyles = css({
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgImage: 'linear-gradient(180deg, var(--colors-ws-hero-top) 0%, var(--colors-ws-hero-bottom) 100%)',
  padding: '24px',
});

function RootErrorBoundary() {
  return (
    <main id="main-content" tabIndex={-1} className={errorPageStyles} lang="fr">
      <SparrowState
        scene={sparrowFlightScene()}
        title={t('route.rootError.title')}
        body={t('route.rootError.body')}
        cta={{ label: t('route.rootError.cta'), onClick: () => window.location.reload() }}
      />
    </main>
  );
}

// Mirrors NotFoundScreen's copy/title, but hard-navigates and skips the AppShell chrome since the route tree itself may be broken.
function RootNotFound() {
  useNotFoundDocumentTitle();
  return (
    <main id="main-content" tabIndex={-1} className={errorPageStyles} lang="fr">
      <SparrowState
        scene={sparrowFlightScene('404')}
        title={NOT_FOUND_COPY.title}
        body={NOT_FOUND_COPY.body}
        cta={{ label: NOT_FOUND_COPY.cta, onClick: () => { window.location.href = '/'; } }}
      />
    </main>
  );
}

export const Route = createRootRouteWithContext<AppRouterContext>()({
  component: () => (
    <>
      <HeadContent />
      <AnnouncerProvider>
        <ToastProvider>
          <Outlet />
          <Toast />
        </ToastProvider>
      </AnnouncerProvider>
    </>
  ),
  errorComponent: RootErrorBoundary,
  notFoundComponent: RootNotFound,
});
